import type { HydratedDocument } from 'mongoose';
import { z } from 'zod';
import { DateSchema, ObjectIdSchema } from './base';
import type { UserDocument } from './user';

const UserHelpRequestSystemInfoSchema = z.object({
  url: z.string().default(''),
  browser: z.string().default(''),
  os: z.string().default(''),
  engine: z.string().optional(),
  device: z.string().optional(),
  userAgent: z.string().optional(),
  timestamp: DateSchema.default(() => new Date()),
});

const UserHelpRequestMessageSchema = z.object({
  turn: z.number().int().nonnegative(),
  role: z.enum(['system', 'user', 'assistant']),
  content: z.string(),
  createdAt: DateSchema.default(() => new Date()),
});

export type UserHelpRequestMessage = z.infer<typeof UserHelpRequestMessageSchema>;

export const UserHelpRequestSchema = z.object({
  id: ObjectIdSchema,
  content: z.string(),
  messages: z.array(UserHelpRequestMessageSchema).default([]),
  source: z.string(),
  type: z.enum(['AIRequest', 'MailRequest']),
  hasBeenRead: z.boolean().default(false),
  creatorId: ObjectIdSchema,
  updaterId: ObjectIdSchema,
  createdAt: DateSchema,
  updatedAt: DateSchema,
  systemInfo: UserHelpRequestSystemInfoSchema.optional(),
});

export type UserHelpRequest = z.infer<typeof UserHelpRequestSchema>;

export interface UserHelpRequestDocumentMethods {
  canEdit(user: UserDocument | Express.User): Promise<boolean>;
  canRead(user: UserDocument | Express.User): Promise<boolean>;
  canRemove(user: UserDocument | Express.User): Promise<boolean>;
}

export type UserHelpRequestDocument = HydratedDocument<
  UserHelpRequest,
  UserHelpRequestDocumentMethods
>;

export const UserHelpRequestPrototypeSchema = UserHelpRequestSchema.pick({
  content: true,
  source: true,
  type: true,
  systemInfo: true,
});

export type UserHelpRequestPrototype = z.infer<typeof UserHelpRequestPrototypeSchema>;
